import { brookCellArtwork } from './73-brook-world.mjs';
export { brookArtworkKey } from './73-brook-world.mjs';
import { creativeCellArtwork, creativeSceneArtwork } from './51-creative-artwork.mjs';
import { scoreCellArtwork } from './49-score-artwork.mjs';
import { jewelArtwork } from './40-jewel-artwork.mjs';
import { blockArtwork } from './43-block-artwork.mjs';
import { ribbonCellArtwork } from './45-ribbon-artwork.mjs';
import { paintFireflyTrail } from './58-firefly-artwork.mjs';

const SVG_NS = 'http://www.w3.org/2000/svg';
const COLOUR = (index) => `var(--game-colour-${index})`;

function svgNode(ctx, tag, attributes = {}, text = '') {
  const node =
    typeof document?.createElementNS === 'function'
      ? document.createElementNS(SVG_NS, tag)
      : ctx.e(tag);
  for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, String(value));
  if (text) node.textContent = text;
  return node;
}
function svg(ctx, className, box = '0 0 100 100') {
  return svgNode(ctx, 'svg', {
    class: className,
    viewBox: box,
    'aria-hidden': 'true',
    focusable: 'false',
  });
}
function add(ctx, host, tag, attributes, text) {
  const node = svgNode(ctx, tag, attributes, text);
  host.append(node);
  return node;
}
function path(ctx, host, d, attributes = {}) {
  return add(ctx, host, 'path', { d, ...attributes });
}
function circle(ctx, host, cx, cy, r, attributes = {}) {
  return add(ctx, host, 'circle', { cx, cy, r, ...attributes });
}

function symbol(ctx, host, glyph, fill = 'var(--game-night)') {
  host.setAttribute('data-symbol', glyph);
  const style = { fill, stroke: fill, 'stroke-width': 1.5, 'stroke-linejoin': 'round' };
  switch (glyph) {
    case '●':
      circle(ctx, host, 50, 50, 14, style);
      break;
    case '▲':
      path(ctx, host, 'M50 31L70 66H30Z', style);
      break;
    case '◆':
      path(ctx, host, 'M50 29L71 50L50 71L29 50Z', style);
      break;
    case '■':
      add(ctx, host, 'rect', { x: 34, y: 34, width: 32, height: 32, rx: 4, ...style });
      break;
    case '★':
      path(ctx, host, 'M50 27L57 42L75 44L61 56L65 74L50 64L35 74L39 56L25 44L43 42Z', style);
      break;
    case '✦':
      path(ctx, host, 'M50 26Q54 46 74 50Q54 54 50 74Q46 54 26 50Q46 46 50 26Z', style);
      break;
    case '⬟':
      path(ctx, host, 'M50 28L72 44L64 70H36L28 44Z', style);
      break;
    case '✚':
      path(ctx, host, 'M44 28H56V44H72V56H56V72H44V56H28V44H44Z', style);
      break;
    default:
      add(
        ctx,
        host,
        'text',
        {
          x: 50,
          y: 54,
          fill,
          'text-anchor': 'middle',
          'dominant-baseline': 'middle',
          'font-size': 34,
          'font-weight': 650,
        },
        glyph,
      );
  }
}

/** Decorative cell art consumes only the authorised derived view. Hidden memory
 * values are never read from engine data; geometry supplements native labels. */
export function gameCellArtwork(ctx, state, cell, index, shape, text) {
  const creative = creativeCellArtwork(ctx, state, cell, index, shape, text, {
    svg,
    add,
    path,
    circle,
    symbol,
  });
  if (creative) return creative;
  const scored = scoreCellArtwork(ctx, state.gameId, cell, shape, {
    svg,
    add,
    path,
    circle,
    symbol,
  });
  if (scored) return scored;
  switch (state.gameId) {
    case 'block-fit':
      return blockArtwork(ctx, cell);
    case 'gem-swap':
      return jewelArtwork(ctx, cell, shape);
    case 'flow-paths':
      return brookCellArtwork(ctx, state, cell, index, { svg, add, path, circle });
    case 'colour-path':
      return ribbonCellArtwork(ctx, state, cell, index, shape);
    case 'thread-loom': {
      const art = svg(ctx, 'game-piece-art game-thread-crossing');
      const weft = () =>
        add(ctx, art, 'rect', { x: 0, y: 30, width: 100, height: 40, fill: COLOUR(cell.weft) });
      const warp = () =>
        add(ctx, art, 'rect', { x: 30, y: 0, width: 40, height: 100, fill: COLOUR(cell.warp) });
      if (cell.over) {
        weft();
        warp();
      } else {
        warp();
        weft();
      }
      path(ctx, art, cell.over ? 'M35 0V100M65 0V100' : 'M0 35H100M0 65H100', {
        fill: 'none',
        stroke: 'var(--game-ink)',
        'stroke-width': 2,
        opacity: 0.25,
      });
      return art;
    }
    case 'pebble-balance': {
      const art = svg(ctx, 'game-piece-art game-pebble-art');
      path(ctx, art, 'M18 59Q12 36 34 23Q56 12 77 32Q94 47 80 69Q62 85 37 78Q22 75 18 59Z', {
        fill: 'var(--game-colour-1)',
        stroke: 'var(--game-line)',
        'stroke-width': 2,
      });
      path(ctx, art, 'M30 38Q44 23 61 31', {
        fill: 'none',
        stroke: 'var(--game-ink)',
        'stroke-width': 3,
        opacity: 0.45,
        'stroke-linecap': 'round',
      });
      add(
        ctx,
        art,
        'text',
        {
          x: 50,
          y: 56,
          'text-anchor': 'middle',
          'font-size': 26,
          'font-weight': 700,
          fill: 'var(--game-night)',
        },
        String(cell.weight),
      );
      add(
        ctx,
        art,
        'text',
        { x: 50, y: 95, 'text-anchor': 'middle', 'font-size': 15, fill: 'var(--game-ink)' },
        ['Choose', 'Left', 'Right'][cell.side],
      );
      return art;
    }
    case 'mosaic-paint':
    case 'pattern-stamp':
    case 'paper-fold': {
      const art = svg(ctx, 'game-piece-art game-mosaic-art');
      add(ctx, art, 'rect', {
        x: 7,
        y: 7,
        width: 86,
        height: 86,
        rx: state.gameId === 'mosaic-paint' ? 8 : 3,
        fill: cell.value >= 0 ? 'var(--piece-colour)' : 'var(--game-glass)',
        stroke: cell.target ? 'var(--game-muted)' : 'var(--game-line)',
        'stroke-width': cell.target ? 3 : 1,
      });
      if (cell.value >= 0) {
        path(ctx, art, 'M17 74V18H74', {
          fill: 'none',
          stroke: 'var(--game-ink)',
          'stroke-width': 3,
          opacity: 0.27,
        });
        symbol(ctx, art, shape, 'var(--game-night)');
      } else if (cell.target) circle(ctx, art, 50, 50, 4, { fill: 'var(--game-muted)' });
      return art;
    }
    case 'cloud-shapes':
    case 'ripple-garden': {
      const art = svg(
        ctx,
        state.gameId === 'cloud-shapes'
          ? 'game-piece-art game-cloud-touch'
          : 'game-piece-art game-pond-touch',
      );
      return art;
    }
    default: {
      const art = ctx.e('span', 'game-piece-label', text);
      return art;
    }
  }
}

function pondScene(ctx, art, scene, previousSerial) {
  const colours = ['var(--game-colour-4)', 'var(--game-colour-2)', 'var(--game-colour-5)'];
  add(ctx, art, 'rect', {
    width: 360,
    height: 360,
    rx: 28,
    fill: colours[scene.theme],
    opacity: 0.17,
  });
  for (const [x, y, radius] of [
    [48, 64, 23],
    [310, 272, 29],
    [78, 292, 14],
  ]) {
    circle(ctx, art, x, y, radius, { fill: 'var(--game-colour-0)', opacity: 0.35 });
    path(ctx, art, `M${x} ${y}L${x + radius} ${y + 4}`, {
      stroke: 'var(--game-night)',
      opacity: 0.4,
      'stroke-width': 3,
    });
  }
  for (const ring of scene.rings) {
    const group = add(ctx, art, 'g', { class: 'game-ripple-rings', 'data-serial': ring.serial });
    for (const radius of [14, 27, 40])
      circle(ctx, group, 36 + ring.x * 72, 36 + ring.y * 72, radius, {
        fill: 'none',
        stroke: 'var(--game-ink)',
        'stroke-width': 1.5,
        opacity: 0.65 - radius / 100,
      });
    if (
      ring.serial > previousSerial &&
      typeof group.animate === 'function' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      group.style.transformBox = 'fill-box';
      group.style.transformOrigin = 'center';
      group.animate(
        [
          { transform: 'scale(.35)', opacity: 0.9 },
          { transform: 'scale(1.35)', opacity: 0.25 },
        ],
        { duration: 1400, easing: 'ease-out' },
      );
    }
  }
}
function cloudScene(ctx, art, scene) {
  add(ctx, art, 'rect', {
    width: 360,
    height: 360,
    rx: 28,
    fill: COLOUR([4, 2, 1][scene.sky]),
    opacity: 0.3,
  });
  circle(ctx, art, 296, 57, 23, { fill: 'var(--game-colour-3)', opacity: 0.8 });
  for (let i = 0; i < scene.board.length; i++)
    if (scene.board[i]) {
      const x = 30 + (i % 6) * 60,
        y = 30 + Math.floor(i / 6) * 60;
      circle(ctx, art, x, y + 4, 38, { fill: 'var(--game-colour-4)', opacity: 0.17 });
      circle(ctx, art, x, y, 36, { fill: 'var(--game-ink)', opacity: 0.92 });
    }
}
function paperScene(ctx, art, scene) {
  add(ctx, art, 'rect', {
    x: 4,
    y: 4,
    width: 352,
    height: 352,
    rx: 8,
    fill: 'var(--game-ink)',
    opacity: 0.08,
  });
  scene.print.forEach((colour, index) => {
    if (colour < 0) return;
    add(ctx, art, 'rect', {
      x: (index % 8) * 44 + 7,
      y: Math.floor(index / 8) * 44 + 7,
      width: 42,
      height: 42,
      rx: 3,
      fill: COLOUR(colour),
      opacity: 0.85,
    });
  });
  path(ctx, art, 'M180 6V354M6 180H354', {
    fill: 'none',
    stroke: 'var(--game-muted)',
    'stroke-dasharray': '4 6',
    opacity: 0.4,
    'stroke-width': 1,
  });
}
function balanceScene(ctx, art, scene) {
  path(ctx, art, 'M180 98L147 210H213Z', { fill: 'var(--game-colour-2)', opacity: 0.6 });
  const tilt = Math.max(-18, Math.min(18, -scene.imbalance * 1.4));
  const beam = add(ctx, art, 'g', {
    transform: `rotate(${tilt} 180 104)`,
    class: 'game-balance-beam',
  });
  path(ctx, beam, 'M59 104H301', {
    stroke: 'var(--game-colour-1)',
    'stroke-width': 9,
    'stroke-linecap': 'round',
  });
  for (const x of [72, 288]) {
    path(ctx, beam, `M${x} 106L${x - 37} 169H${x + 37}Z`, {
      fill: 'none',
      stroke: 'var(--game-muted)',
      'stroke-width': 2,
    });
    path(ctx, beam, `M${x - 41} 169Q${x} 208 ${x + 41} 169Z`, {
      fill: 'var(--game-glass)',
      stroke: 'var(--game-colour-1)',
      'stroke-width': 3,
    });
  }
  circle(ctx, art, 180, 104, 10, { fill: 'var(--game-colour-3)' });
  for (const [x, value] of [
    [72, scene.left],
    [288, scene.right],
  ])
    add(
      ctx,
      art,
      'text',
      {
        x,
        y: 248,
        'text-anchor': 'middle',
        fill: 'var(--game-ink)',
        'font-size': 24,
        'font-weight': 650,
      },
      String(value),
    );
}

/** Paint the noninteractive family scene beneath native controls. The caller
 * owns mounting/lifecycle; this function schedules no frames and reads only the
 * supplied derived scene. SVG is a complete usable fallback for optional WebGL. */
export function paintSceneArtwork(ctx, host, view, state) {
  host.dataset.game = state.gameId;
  host.dataset.family = view.family || state.gameId;
  host.setAttribute('aria-hidden', 'true');
  if (view.family === 'hanoi') {
    host.replaceChildren();
    return;
  }
  if (state.gameId === 'number-trail') {
    paintFireflyTrail(ctx, host, view, { svg, add, path, circle, symbol }, state.moves);
    return;
  }
  if (creativeSceneArtwork(ctx, host, state, { svg, add, path, circle, symbol })) return;
  const art = svg(ctx, 'game-scene-art', '0 0 360 360');
  switch (state.gameId) {
    case 'ripple-garden':
      pondScene(ctx, art, view.scene, host.gameSceneSerial ?? 0);
      host.gameSceneSerial = view.scene.serial;
      break;
    case 'cloud-shapes':
      cloudScene(ctx, art, view.scene);
      break;
    case 'paper-fold':
      paperScene(ctx, art, view.scene);
      break;
    case 'pebble-balance':
      balanceScene(ctx, art, view.scene);
      break;
    case 'thread-loom':
      add(ctx, art, 'rect', {
        x: 12,
        y: 12,
        width: 336,
        height: 336,
        rx: 16,
        fill: 'none',
        stroke: 'var(--game-colour-1)',
        'stroke-width': 12,
        opacity: 0.45,
      });
      for (let index = 0; index < 6; index++) {
        path(ctx, art, `M${48 + index * 53} 4V356`, {
          stroke: COLOUR(view.scene.columns[index]),
          'stroke-width': 3,
          opacity: 0.8,
        });
        path(ctx, art, `M4 ${48 + index * 53}H356`, {
          stroke: COLOUR(view.scene.rows[index]),
          'stroke-width': 3,
          opacity: 0.8,
        });
      }
      break;
    default:
      host.replaceChildren();
      return;
  }
  host.replaceChildren(art);
}
